/**
 * Servicio de API para ubicaciones
 * Sprint 2 - Integración Frontend-Backend
 * 
 * Maneja el envío de ubicaciones al servidor
 */

import { apiClient } from './api';
import type { LocationData, LocationApiResponse } from '../types';

/**
 * Respuesta del historial de ubicaciones
 */
interface LocationHistoryResponse {
    message: string;
    data: LocationApiResponse['data'][];
    meta: {
        count: number;
        limit: number;
    };
}

/**
 * Respuesta de última ubicación (puede ser null)
 */
interface LastLocationResponse {
    message: string;
    data: LocationApiResponse['data'] | null;
}

/**
 * Envía una ubicación al servidor
 * @param location Datos de ubicación del dispositivo
 * @returns Respuesta del servidor con la ubicación guardada
 */
export async function sendLocation(location: LocationData): Promise<LocationApiResponse> {
    const payload = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        altitude: location.altitude,
        speed: location.speed,
        heading: location.heading,
        battery_level: location.battery_level ?? null,
        timestamp: location.timestamp,
    };

    console.log('[LocationAPI] Sending location to server:', payload);

    try {
        const response = await apiClient.post<LocationApiResponse>('/api/location', payload);
        console.log('[LocationAPI] Location sent successfully:', response);
        return response;
    } catch (error) {
        console.error('[LocationAPI] Error sending location:', error);
        throw error;
    }
}

/**
 * Obtiene la última ubicación registrada en el servidor
 * @returns Última ubicación o null si no hay ninguna
 */
export async function getLastLocation(): Promise<LastLocationResponse> {
    return apiClient.get<LastLocationResponse>('/api/location/last');
}

/**
 * Obtiene el historial de ubicaciones
 * @param limit Número máximo de ubicaciones a obtener (default: 10, max: 100)
 * @returns Lista de ubicaciones ordenadas por más reciente
 */
export async function getLocationHistory(limit: number = 10): Promise<LocationHistoryResponse> {
    return apiClient.get<LocationHistoryResponse>(`/api/location/history?limit=${limit}`);
}

// Exportar como objeto para consistencia
export const locationApiService = {
    sendLocation,
    getLastLocation,
    getLocationHistory,
};
